import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Brand } from "../components/Shell";
import { Lock, Loader2, ShieldCheck, KeyRound, AlertTriangle } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [state, setState] = useState("checking"); // checking | form | invalid | done
  const [pw, setPw] = useState({ password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    api.get("/auth/forgot-password/validate", { params: { token } })
      .then(() => setState("form"))
      .catch(() => setState("invalid"));
  }, [token]);

  const submit = async () => {
    if (pw.password.length < 6) { setErr("Password must be at least 6 characters"); return; }
    if (pw.password !== pw.confirm) { setErr("Passwords do not match"); return; }
    setErr(""); setLoading(true);
    try {
      await api.post("/auth/forgot-password/reset", { reset_token: token, new_password: pw.password });
      setState("done");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail);
      if (e.response?.status === 400 && /invalid|expired/i.test(msg)) setState("invalid");
      else setErr(msg);
    } finally { setLoading(false); }
  };

  const inputCls = "w-full h-12 pl-12 pr-4 rounded-2xl bg-white/5 border border-white/10 text-white placeholder:text-slate-500 text-sm focus:outline-none focus:border-rose-500/60 focus:ring-2 focus:ring-rose-500/20 transition-all";

  return (
    <div className="flex-1 overflow-y-auto no-scrollbar relative" data-testid="reset-password-page">
      <div className="absolute inset-0 pointer-events-none opacity-60" style={{ background: "radial-gradient(circle at 50% -10%, rgba(255,46,99,0.28), transparent 55%)" }} />
      <div className="relative px-7 pt-14 pb-8 min-h-full flex flex-col">
        <div className="flex flex-col items-center text-center gap-3 mb-8">
          <Brand size="lg" />
          <div className="w-12 h-12 rounded-2xl bg-rose-500/15 grid place-items-center">
            {state === "done" ? <ShieldCheck className="w-6 h-6 text-emerald-400" />
              : state === "invalid" ? <AlertTriangle className="w-6 h-6 text-amber-400" />
              : <KeyRound className="w-6 h-6 text-rose-400" />}
          </div>
          <h1 className="text-2xl font-extrabold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
            {state === "done" ? "Password updated" : state === "invalid" ? "Link expired" : "Create new password"}
          </h1>
          <p className="text-slate-400 text-sm max-w-[18rem]">
            {state === "done" && "Your password has been changed successfully. Sign in with your new password."}
            {state === "invalid" && "This reset link is invalid or has expired. Please request a new one."}
            {state === "form" && "Choose a new password for your GulMatch account."}
          </p>
        </div>

        {state === "checking" && (
          <div className="py-10 flex justify-center"><Loader2 className="w-7 h-7 text-rose-500 animate-spin" /></div>
        )}

        {state === "form" && (
          <div className="flex flex-col gap-3.5">
            {err && <p data-testid="reset-error" className="text-sm text-rose-400 text-center">{err}</p>}
            <div className="relative">
              <Lock className="w-5 h-5 text-slate-500 absolute left-4 top-1/2 -translate-y-1/2" />
              <input data-testid="reset-new-password" type="password" placeholder="New password"
                value={pw.password} onChange={(e) => setPw({ ...pw, password: e.target.value })} className={inputCls} />
            </div>
            <div className="relative">
              <Lock className="w-5 h-5 text-slate-500 absolute left-4 top-1/2 -translate-y-1/2" />
              <input data-testid="reset-confirm-password" type="password" placeholder="Confirm new password"
                value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className={inputCls} />
            </div>
            <button data-testid="reset-submit" onClick={submit} disabled={loading}
              className="mt-2 h-12 rounded-2xl bg-gradient-to-r from-[#FF2E63] to-[#FF6B8B] text-white font-bold flex items-center justify-center gap-2 shadow-lg shadow-rose-500/30 active:scale-[0.98] transition-transform disabled:opacity-60">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <KeyRound className="w-5 h-5" />}
              Update password
            </button>
          </div>
        )}

        {(state === "done" || state === "invalid") && (
          <button data-testid="reset-to-login" onClick={() => navigate("/auth", { replace: true })}
            className="h-12 rounded-2xl bg-gradient-to-r from-[#FF2E63] to-[#FF6B8B] text-white font-bold flex items-center justify-center gap-2 shadow-lg shadow-rose-500/30 active:scale-[0.98] transition-transform">
            {state === "done" ? "Back to Sign In" : "Request a new link"}
          </button>
        )}
      </div>
    </div>
  );
}
